import React from 'react'

import { AcornsIcon, ETFIcons } from '@acorns/icons'
import { Heading2, SecurityLogo } from '@acorns/web-components'
import { useRemoteStrings } from '@acorns/web-utils'
import type { GetRemoteStringFunction } from '@acorns/web-utils'
import styled from 'styled-components'

import { SecurityType } from 'generated/types'
import { style } from 'theme'

import { getFormattedDecimalPercentage } from '../SharedFunctions'

const CUSTOM_WIDTH_MAX = '842px'
const CUSTOM_WIDTH_MIN = '750px'

type Props = {
  disabled: boolean
  item: any
  onPress?: (item: any) => void
  hasPercentage?: boolean
  testIdPrefix: string
}

const ItemContainer = styled.div<{
  disabled: boolean
}>`
  display: flex;
  align-items: center;
  height: 88px;
  cursor: pointer;
  @media screen and (max-width: ${style((theme) => theme.breakpoints.sm)}px) {
    padding: 0px 30px;
  }

  ${(props) =>
    props.disabled &&
    `
      color: ${style((theme) => theme.colors.white)};
      opacity: 0.4;
    `}
  @media screen and (max-width: ${660}px) {
    justify-content: center;
  }
`

const Value = styled.div`
  font-size: 14px;
  color: ${style((theme) => theme.colors.green)};
  font-weight: 600;
  margin-right: 13px;
`

const Label = styled.div<{ isBold?: boolean }>`
  font-size: 14px;
  color: ${style((theme) => theme.colors.slate)};
  overflow: hidden;
  margin-right: auto;
  ${(props) =>
    props.isBold &&
    `
    font-weight: 600;
  `};
`

const Symbol = styled(Heading2)`
  font-size: 12px;
  line-height: 16px;
  margin-top: 4px;
  color: ${style((theme) => theme.colors.stone)};
`

const TitleContainer = styled.div<{ hasPercentage: boolean | undefined }>`
  display: flex;
  flex-direction: column;
  ${(props) => (props.hasPercentage ? 'width: 156px;' : 'width: 195px;')}
  margin-right: 20px;
  @media screen and (min-width: ${CUSTOM_WIDTH_MIN}) and (max-width: ${CUSTOM_WIDTH_MAX}) {
    width: 149px;
  }
`

const IconContainer = styled.div<{ hasPercentage: boolean | undefined }>`
  ${(props) =>
    props.hasPercentage &&
    `
      display: flex;
      width: 53px;
      justify-content: flex-end;
    `}
  @media screen and (min-width: ${CUSTOM_WIDTH_MIN}) and (max-width: ${CUSTOM_WIDTH_MAX}) {
    margin-left: auto;
  }
`

const LogoContainer = styled.div`
  margin-right: 15px;
`

const InfoContainer = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
`

const getIcon = (
  symbol: string,
  logoUrl: string,
  isEtf: boolean,
  getRemoteString: GetRemoteStringFunction,
) => {
  const etfIconKey = ETFIcons[symbol]

  if (etfIconKey) {
    return (
      <LogoContainer>
        <AcornsIcon icon={etfIconKey} width="68px" height="68px" />
      </LogoContainer>
    )
  } else {
    return (
      <LogoContainer>
        <SecurityLogo
          logoUrl={logoUrl}
          securitySymbol={
            isEtf
              ? getRemoteString({
                  key: 'global.etf',
                })
              : symbol
          }
          width={68}
          height={68}
          isEtf={isEtf}
        />
      </LogoContainer>
    )
  }
}

const GridItem = (props: Props) => {
  const { getRemoteString } = useRemoteStrings()

  const handleClick = (item: any) => {
    if (props.onPress) {
      props.onPress(item)
    }
  }
  return (
    <ItemContainer
      disabled={props.disabled}
      key={props.item.symbol}
      onClick={() => handleClick(props.item)}
      data-testid={props.testIdPrefix + '_' + props.item.symbol}
    >
      <InfoContainer>
        {getIcon(
          props.item.symbol,
          props.item.security?.profile?.logoUrl ?? '',
          props.item.security?.type === SecurityType.Etf,
          getRemoteString,
        )}
        <TitleContainer hasPercentage={props.hasPercentage}>
          <Label isBold={true}>
            {props.item?.security?.displayName ?? props.item?.displayName}
          </Label>
          <Symbol>{props.item.symbol}</Symbol>
        </TitleContainer>
      </InfoContainer>

      <IconContainer hasPercentage={props.hasPercentage}>
        {props.hasPercentage && (
          <Value>
            {getFormattedDecimalPercentage(props.item.allocationPercent, false)}
          </Value>
        )}
        <AcornsIcon
          icon={AcornsIcon.Icon.ChevronRightPortfolio}
          width="12px"
          height="12px"
          style={{ marginTop: '1px', padding: '2px' }}
        />
      </IconContainer>
    </ItemContainer>
  )
}

export default GridItem
